import React, { useEffect } from 'react';
import { Cloud, CheckCircle, AlertCircle } from 'lucide-react';
import { useAutoMigration } from '../../hooks/useAutoMigration';

interface AutoMigrationNotificationProps {
  rut?: string;
  showStats?: boolean;
}

/**
 * Componente para mostrar notificaciones de migración automática
 * 
 * Se renderiza en la parte superior de la aplicación cuando hay
 * documentos pendientes de migración a Google Drive
 */
export const AutoMigrationNotification: React.FC<AutoMigrationNotificationProps> = ({
  rut,
  showStats = false,
}) => {
  const { migrando, stats, error } = useAutoMigration(rut, true);

  // No mostrar nada si no hay RUT o no hay datos
  if (!rut || !stats) return null;

  // No mostrar si todo está migrado y no hay error
  if (!migrando && stats.pendientes === 0 && !error && !showStats) return null;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      {/* Notificación de migración en progreso */}
      {migrando && stats.pendientes > 0 && (
        <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 p-4 shadow-sm animate-pulse">
          <div className="flex items-center">
            <Cloud className="h-5 w-5 text-blue-600 mr-3 animate-bounce" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-800">
                Sincronizando documentos con Google Drive
              </h3>
              <p className="text-sm text-blue-700 mt-1">
                {stats.pendientes} documento{stats.pendientes !== 1 ? 's' : ''} en proceso de migración...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Notificación de error */}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-4 shadow-sm">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-3" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">
                Error en sincronización
              </h3>
              <p className="text-sm text-red-700 mt-1">
                {error}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Estadísticas (opcional) */}
      {showStats && stats && !migrando && stats.pendientes === 0 && (
        <div className="mb-4 rounded-lg bg-green-50 border border-green-200 p-4 shadow-sm">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-800">
                Documentos sincronizados
              </h3>
              <p className="text-sm text-green-700 mt-1">
                {stats.en_drive} de {stats.total} documentos en Google Drive ({stats.porcentaje_migrado}%)
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoMigrationNotification;
